import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";

interface KpiTileProps {
  value: string;
  label: string;
  /** Change vs the previous period. Sign drives direction. `inverted` flips
   * the color polarity (e.g. for cost, where a *decrease* is positive). */
  delta?: { value: number; suffix?: string; inverted?: boolean };
  /** Free-form caption shown next to the delta, e.g. "vs last week". */
  caption?: string;
}

export function KpiTile({ value, label, delta, caption }: KpiTileProps) {
  const trend =
    delta === undefined
      ? "flat"
      : delta.value > 0
        ? "up"
        : delta.value < 0
          ? "down"
          : "flat";

  // For "inverted" metrics (cost), down is good.
  const isPositive = delta?.inverted ? trend === "down" : trend === "up";

  const Icon =
    trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : Minus;

  const trendTextClass =
    trend === "flat"
      ? "text-[var(--color-slate-300)]"
      : isPositive
        ? "text-[var(--color-mint)]"
        : "text-[var(--color-ember)]";

  const trendBgClass =
    trend === "flat"
      ? "bg-[var(--color-graphite-700)]"
      : isPositive
        ? "bg-[var(--color-mint)]/10"
        : "bg-[var(--color-ember)]/10";

  const formatDelta = (n: number, suffix?: string) => {
    const abs = Math.abs(n);
    const sign = n > 0 ? "+" : n < 0 ? "−" : "";
    const formatted =
      abs >= 1 || abs === 0 ? abs.toLocaleString() : abs.toFixed(2);
    return `${sign}${formatted}${suffix ?? ""}`;
  };

  return (
    <Card ring className="flex flex-col gap-3 p-5 min-h-[140px]">
      {/* Label up top in mono caps — anchors the tile */}
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-slate-200)]">
        {label}
      </p>

      {/* The number itself */}
      <p className="text-[32px] leading-none font-semibold tracking-[-0.04em] text-[var(--color-snow)] tabular-nums">
        {value}
      </p>

      {/* Delta + context at the bottom — the delta is the visual anchor */}
      {(delta !== undefined || caption) && (
        <div className="mt-auto flex items-center gap-2 flex-wrap">
          {delta !== undefined && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-[var(--radius-badge)]",
                "px-1.5 h-[20px] font-mono text-[11px] font-medium tabular-nums",
                trendBgClass,
                trendTextClass,
              )}
            >
              <Icon size={11} strokeWidth={2.4} />
              {formatDelta(delta.value, delta.suffix)}
            </span>
          )}
          {caption && (
            <span className="text-[12px] text-[var(--color-slate-300)]">
              {caption}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
